import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';

const ID = 'direct_inps499_20261007_v1';
const CID = 'con_46e2149840c1';
const canonical = value => JSON.stringify(value, function (key, item) {
  return item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(k => [k, item[k]])) : item;
});
async function readAnalysis() {
  const { data } = await api.get('/admin/ai-pipeline/bandi');
  const analysis = data.items?.find(a => a.analysis_id === ID && a.contest_id === CID);
  if (!analysis) throw new Error('Programma importato non trovato. Nessuna modifica eseguita.');
  return analysis;
}

export default function Inps499ProgramReview({ report, onSaved }) {
  const [analysis, setAnalysis] = useState(null);
  const [documentsChecked, setDocumentsChecked] = useState(false);
  const [programChecked, setProgramChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let active = true;
    readAnalysis().then(a => { if (active) setAnalysis(a); })
      .catch(() => { if (active) setError('Impossibile leggere il programma salvato. Ricarica la pagina.'); });
    return () => { active = false; };
  }, []);
  const matches = analysis && canonical(analysis.program) === canonical(report.program);
  const approved = matches && analysis.status === 'APPROVED' && !!analysis.approved_by;
  async function approve(event) {
    event.preventDefault();
    if (!matches || approved || busy || !documentsChecked || !programChecked) return;
    setBusy(true); setError(''); setSaved(false);
    try {
      const fresh = await readAnalysis();
      if (canonical(fresh.program) !== canonical(analysis.program) || fresh.raw_hash !== analysis.raw_hash) {
        throw new Error('Il programma è cambiato: ricarica la pagina e verifica la nuova versione.');
      }
      await api.put(`/admin/ai-pipeline/bandi/${ID}/program`, fresh.program);
      const after = await readAnalysis();
      if (after.status !== 'APPROVED' || !after.approved_by || canonical(after.program) !== canonical(fresh.program)) {
        throw new Error('Esito da verificare: ricarica lo stato prima di riprovare.');
      }
      setAnalysis(after); setSaved(true); await onSaved();
    } catch (e) { setError(e.message || 'Approvazione non confermata. Ricarica lo stato.'); }
    finally { setBusy(false); }
  }
  return <section className="surface p-5 space-y-4" id="approvazione-programma">
    <h2 className="text-xl font-semibold">Passaggio successivo: approvazione del programma</h2>
    <p className="text-sm">Le revisioni delle 44 unità restano salvate. Questa decisione separata riguarda il programma estratto dal bando, non autorizza le fonti e non pubblica il corso.</p>
    {error && <p role="alert" className="text-red-800">{error}</p>}
    {analysis && !matches && <p role="alert">Il programma salvato differisce dal pacchetto revisionato. Approvazione bloccata: serve una verifica tecnica.</p>}
    <p><strong>{report.program.contest_name}</strong> · {report.program.positions} posti</p>
    <p className="text-sm">Profilo: {report.program.profile}</p>
    <ul className="list-disc pl-5 text-sm">{(report.program.exams || []).map(e => <li key={e}>{e}</li>)}</ul>
    <div className="flex flex-wrap gap-4">{(report.program.attachments || []).filter(url => url.startsWith('https://')).map((url, i) =>
      <a key={url} className="text-brand underline" href={url} target="_blank" rel="noopener noreferrer">Documento ufficiale {i + 1} ↗</a>)}</div>
    <details><summary className="cursor-pointer font-medium">Verifica le 16 voci e le unità associate</summary>
      <ol className="list-decimal pl-5 mt-3 space-y-3">{report.program.scope_requirements.map(r => <li key={r.requirement_id}>
        <strong>{r.quote}</strong><p className="text-sm">{r.locator}</p>
        <ul className="list-disc pl-5 text-sm">{(report.coverage[r.requirement_id] || []).map(sid => <li key={sid}>{report.units.find(u => u.section_id === sid)?.title || sid}</li>)}</ul>
      </li>)}</ol>
    </details>
    <details><summary className="cursor-pointer">Programma completo registrato</summary><pre className="whitespace-pre-wrap break-words text-xs mt-3">{JSON.stringify(report.program, null, 2)}</pre></details>
    {approved ? <p role="status" className="text-green-800">Programma approvato con account autenticato. Restano separati i controlli su bando originale, fonti, perimetro ed approvazione finale.</p> :
      <form onSubmit={approve} className="space-y-4">
        <label className="flex gap-3 items-start"><input type="checkbox" checked={documentsChecked} onChange={e => setDocumentsChecked(e.target.checked)} />Ho confrontato il programma con il bando ufficiale e la rettifica collegati.</label>
        <label className="flex gap-3 items-start"><input type="checkbox" checked={programChecked} onChange={e => setProgramChecked(e.target.checked)} />Confermo profilo, prova e tutte le 16 voci del programma qui visualizzato.</label>
        <Button type="submit" disabled={!matches || busy || !documentsChecked || !programChecked}>{busy ? 'Registrazione…' : 'Approva il programma INPS 499'}</Button>
      </form>}
    {saved && <p role="status">Approvazione del programma verificata. Nessuna lezione, quiz o schema modificato; nessun job avviato.</p>}
  </section>;
}
